import { useMemo } from "react";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

export type WhitelabelPackageCard = {
  packageId: string;
  name?: string;
  pointsCredited: number;
  payAmount: number;
  discountAmount?: number;
  originalPayAmount?: number;
  currency?: string;
  /** Discount percentage from API (e.g. 10 = 10%). */
  packageDiscountPercent?: number;
  /** From POST /whitelabel/packages — enabled payment modes for this plan. */
  paymentMethods?: string[];
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  CRYPTO: "Crypto",
  FIAT: "UPI/Bank transfer",
  UPI: "UPI",
  BANK_TRANSFER: "Bank transfer",
  OFFLINE: "Offline",
  UPI_BANK: "UPI / Bank",
};

function formatPaymentMethodLabel(code: string): string {
  const u = String(code ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (!u) return "";
  if (PAYMENT_METHOD_LABELS[u]) return PAYMENT_METHOD_LABELS[u];
  return u
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

/** Normalize `paymentMethods` / `payment_methods` from a package row returned by POST /whitelabel/packages. */
export function parseWhitelabelPackagePaymentMethods(raw: unknown): string[] | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const v = (raw as Record<string, unknown>).paymentMethods ?? (raw as Record<string, unknown>).payment_methods;
  if (!Array.isArray(v)) return undefined;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of v) {
    const s = String(item ?? "").trim();
    if (!s) continue;
    const k = s.toUpperCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out.length ? out : undefined;
}

type WhitelabelPackagePickerProps = {
  packages: WhitelabelPackageCard[];
  selectedPackageId: string | null;
  onSelectPackage: (packageId: string) => void;
  formatPrice: (amount: number, currency?: string) => string;
  formatPoints: (n: number) => string;
};

export function WhitelabelPackagePicker({
  packages,
  selectedPackageId,
  onSelectPackage,
  formatPrice,
  formatPoints,
}: WhitelabelPackagePickerProps) {
  const formatPriceNoDecimals = (amount: number, currency = "INR") => {
    try {
      const locale = currency === "INR" ? "en-IN" : undefined;
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      // Keep previous formatter as fallback for unknown/invalid currency codes.
      return formatPrice(Math.round(amount), currency);
    }
  };

  const sorted = useMemo(
    () => [...packages].sort((a, b) => a.payAmount - b.payAmount),
    [packages],
  );

  if (packages.length === 0) return null;

  return (
    <div className="mt-2">
      <div
        className="flex gap-3 overflow-x-auto scroll-smooth px-0.5 pb-1 pt-0.5 [-webkit-overflow-scrolling:touch]"
        role="radiogroup"
        aria-label="Choose a package"
        style={{ scrollbarWidth: "thin" }}
      >
        {sorted.map((pkg) => {
          const selected = selectedPackageId === pkg.packageId;
          const cur = pkg.currency || "INR";
          const discountFromField =
            pkg.packageDiscountPercent != null && !Number.isNaN(pkg.packageDiscountPercent)
              ? Math.max(0, Math.round(pkg.packageDiscountPercent))
              : 0;
          const hasValidOriginalAmount =
            typeof pkg.originalPayAmount === "number" &&
            Number.isFinite(pkg.originalPayAmount) &&
            pkg.originalPayAmount > pkg.payAmount;
          const effectiveDiscount = hasValidOriginalAmount
            ? Math.max(
                0,
                Math.round(((pkg.originalPayAmount! - pkg.payAmount) / pkg.originalPayAmount!) * 100),
              )
            : discountFromField;

          return (
            <div
              key={pkg.packageId}
              role="radio"
              aria-checked={selected}
              tabIndex={0}
              onClick={() => onSelectPackage(pkg.packageId)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectPackage(pkg.packageId);
                }
              }}
              className={`group relative flex w-[min(100%,210px)] shrink-0 cursor-pointer flex-col overflow-hidden rounded-2xl border-2 p-3.5 transition-all duration-300 ease-out motion-reduce:transition-colors ${
                selected
                  ? "scale-[1.02] border-[color:var(--border-color)] bg-[color-mix(in_srgb,var(--border-color)_14%,#0f172a)] shadow-[0_14px_42px_-14px_color-mix(in_srgb,var(--border-color)_55%,transparent),inset_0_1px_0_0_color-mix(in_srgb,var(--border-color)_35%,transparent)]"
                  : "border-[color-mix(in_srgb,var(--border-color)_28%,#3f4f52)] bg-gradient-to-br from-zinc-800/95 via-[color-mix(in_srgb,var(--border-color)_8%,#1a2332)] to-zinc-950/90 shadow-[0_8px_28px_-8px_rgba(0,0,0,0.45),inset_0_1px_0_0_rgba(255,255,255,0.06)] ring-1 ring-inset ring-white/[0.06] hover:-translate-y-1 hover:scale-[1.01] hover:border-[color-mix(in_srgb,var(--border-color)_50%,#4b5563)] hover:shadow-[0_18px_44px_-14px_color-mix(in_srgb,var(--border-color)_28%,rgba(0,0,0,0.6))] hover:ring-white/10"
              }`}
            >
              <div
                className={`pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.05] via-transparent to-transparent opacity-100 transition-opacity duration-300 group-hover:from-white/[0.07] ${selected ? "opacity-40" : ""}`}
                aria-hidden
              />
              <div
                className={`pointer-events-none absolute -right-7 -top-10 h-28 w-28 rounded-full blur-2xl transition-opacity duration-300 ${
                  selected
                    ? "bg-[color-mix(in_srgb,var(--border-color)_45%,transparent)] opacity-65"
                    : "bg-[color-mix(in_srgb,var(--border-color)_34%,transparent)] opacity-30 group-hover:opacity-50"
                }`}
                aria-hidden
              />
              <div
                className="pointer-events-none absolute -bottom-9 -left-8 h-20 w-20 rounded-full bg-cyan-400/10 blur-xl opacity-55"
                aria-hidden
              />
              <div
                className={`pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r transition-opacity duration-300 ${
                  selected
                    ? "from-transparent via-[color:var(--border-color)] to-transparent opacity-100"
                    : "from-transparent via-[color:color-mix(in_srgb,var(--border-color)_65%,transparent)] to-transparent opacity-50 group-hover:opacity-90"
                }`}
                aria-hidden
              />
              {selected ? (
                <div
                  className="pointer-events-none absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-md border border-emerald-500 bg-emerald-500 text-white shadow-[0_0_0_1px_rgba(16,185,129,0.35),0_4px_14px_-6px_rgba(16,185,129,0.9)]"
                  aria-hidden
                >
                  <CheckRoundedIcon sx={{ fontSize: 13 }} />
                </div>
              ) : null}

              <h3 className="relative text-base font-semibold tracking-tight text-zinc-100">
                <span
                  className={`inline-block border-b-2 pb-0.5 transition-colors duration-300 ${
                    selected
                      ? "border-[color:var(--border-color)]"
                      : "border-zinc-500/45 group-hover:border-[color-mix(in_srgb,var(--border-color)_55%,#71717a)]"
                  }`}
                >
                  {pkg.name?.trim() || "Plan"}
                </span>
              </h3>

              <div
                className={`relative mt-2.5 space-y-2.5 text-sm leading-snug ${selected ? "text-zinc-400" : "text-zinc-300"}`}
              >
                <div className="flex gap-3">
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-colors duration-300 ${
                      selected
                        ? "border-transparent bg-[color-mix(in_srgb,var(--border-color)_22%,#0f172a)] text-[color:var(--border-color)]"
                        : "border-white/10 bg-zinc-700/85 text-zinc-200 shadow-inner shadow-black/20 group-hover:border-[color-mix(in_srgb,var(--border-color)_35%,transparent)] group-hover:bg-zinc-600/90 group-hover:text-[color:var(--border-color)]"
                    }`}
                  >
                    <StarsRoundedIcon sx={{ fontSize: 20 }} aria-hidden />
                  </span>
                  <p className="min-w-0 pt-1 font-semibold text-[color:var(--border-color)]">
                    {formatPoints(pkg.pointsCredited)} points
                  </p>
                </div>
                <div className="flex gap-3">
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-colors duration-300 ${
                      selected
                        ? "border-transparent bg-[color-mix(in_srgb,var(--border-color)_22%,#0f172a)] text-[color:var(--border-color)]"
                        : "border-white/10 bg-zinc-700/85 text-zinc-200 shadow-inner shadow-black/20 group-hover:border-[color-mix(in_srgb,var(--border-color)_35%,transparent)] group-hover:bg-zinc-600/90 group-hover:text-[color:var(--border-color)]"
                    }`}
                  >
                    <PaidRoundedIcon sx={{ fontSize: 20 }} aria-hidden />
                  </span>
                  <div
                    className={`min-w-0 ${hasValidOriginalAmount ? "pt-0.5" : "flex min-h-9 items-center"}`}
                  >
                    <p className="leading-tight font-semibold text-zinc-100">
                      {formatPriceNoDecimals(pkg.payAmount, cur)}
                    </p>
                    {hasValidOriginalAmount ? (
                      <p className="mt-0.5 flex items-baseline gap-2 text-xs leading-tight">
                        <span className="text-zinc-400 line-through">
                          {formatPriceNoDecimals(pkg.originalPayAmount!, cur)}
                        </span>
                        {effectiveDiscount > 0 ? (
                          <span className="font-medium text-emerald-300">
                            Save {effectiveDiscount}%
                          </span>
                        ) : null}
                      </p>
                    ) : null}
                  </div>
                </div>
                {pkg.paymentMethods && pkg.paymentMethods.length > 0 ? (
                  <div className="border-t border-white/[0.08] pt-2.5">
                    <p className="text-[10px] font-semibold tracking-wide text-zinc-500">
                      Payment modes
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {pkg.paymentMethods.map((m) => {
                        const label = formatPaymentMethodLabel(m);
                        if (!label) return null;
                        return (
                          <span
                            key={`${pkg.packageId}-${m}`}
                            className={`inline-flex max-w-full truncate rounded-md border px-2 py-0.5 text-[11px] font-medium leading-tight ${
                              selected
                                ? "border-[color-mix(in_srgb,var(--border-color)_40%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_12%,#0f172a)] text-zinc-200"
                                : "border-white/12 bg-zinc-900/50 text-zinc-300"
                            }`}
                          >
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}
